import {match} from 'ts-pattern';

import type {
  FeatureResponseBody,
  FeatureRuleResponseBody,
  TrimResponseBody,
} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';

const tValues = (key: string, options: {defaultValue: string; [value: string]: string | number}) =>
  i18n.t(key, options);

/**
 * The rule as one sentence, prefixed with the trims it applies to. A stored trim that is archived or
 * not among the model year's `trims` is left out of the names.
 */
export const ruleSentence = (
  rule: Pick<FeatureRuleResponseBody, 'type' | 'sourceFeatureId' | 'targetFeatureIds' | 'trimIds'>,
  features: FeatureResponseBody[],
  trims: Pick<TrimResponseBody, 'id' | 'name' | 'archived'>[]
) => {
  const name = (id: string) => features.find((item) => item.id === id)?.name ?? id;
  const source = name(rule.sourceFeatureId);
  const targets = rule.targetFeatureIds.map(name).join(', ');
  const sentence = match(rule.type)
    .with('requires', () =>
      tValues('entity.newVehicle.rules.requiresSentence', {
        defaultValue: '{source} requires {targets} — add it first',
        source,
        targets,
      })
    )
    .with('excludes', () =>
      tValues('entity.newVehicle.rules.excludesSentence', {
        defaultValue: '{source} cannot be combined with {targets}',
        source,
        targets,
      })
    )
    .with('autoAdd', () =>
      tValues('entity.newVehicle.rules.autoAddSentence', {
        defaultValue: 'Adds {targets} when {source} is selected',
        source,
        targets,
      })
    )
    .exhaustive();
  const trimNames = trims
    .filter(({id, archived}) => !archived && rule.trimIds.includes(id))
    .map((trim) => trim.name);
  return match({isEveryTrim: rule.trimIds.length === 0, trimNames})
    .with({isEveryTrim: true}, () =>
      tValues('entity.newVehicle.rules.onAllTrimsSentence', {
        defaultValue: 'On all trims: {sentence}',
        sentence,
      })
    )
    .with({trimNames: []}, () =>
      tValues('entity.newVehicle.rules.onNoCurrentTrimSentence', {
        defaultValue: 'On no current trim: {sentence}',
        sentence,
      })
    )
    .otherwise(() =>
      tValues('entity.newVehicle.rules.onTrimsSentence', {
        defaultValue: 'On {trims}: {sentence}',
        trims: trimNames.join(', '),
        sentence,
      })
    );
};
